import * as amplitude from '@amplitude/unified';
import { Identify } from '@amplitude/analytics-browser';

const AMPLITUDE_API_KEY = '8b7554c8ad2641aa4994f1a9fe21a5ef';

const AMPLITUDE_OPTIONS = {
  analytics: {
    autocapture: {
      attribution: true,
      fileDownloads: true,
      formInteractions: true,
      pageViews: true,
      sessions: true,
      elementInteractions: true,
      networkTracking: true,
      webVitals: true,
      frustrationInteractions: true,
    },
  },
  sessionReplay: {
    sampleRate: 1,
  },
};

let amplitudeInitialized = false;

export const initAnalytics = () => {
  if (typeof window === 'undefined' || amplitudeInitialized) {
    return;
  }

  amplitude.initAll(AMPLITUDE_API_KEY, AMPLITUDE_OPTIONS);
  amplitudeInitialized = true;
};

export const trackAmplitudeEvent = (eventName: string, params?: Record<string, any>) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (!amplitudeInitialized) {
    initAnalytics();
  }

  if (params) {
    amplitude.track(eventName, params);
  } else {
    amplitude.track(eventName);
  }
};

const simpleHash = (value: string) => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash).toString(16);
};

export const hashIdentifier = async (value: string) => {
  if (typeof window === 'undefined') {
    return simpleHash(value);
  }

  if (typeof crypto?.subtle?.digest === 'function') {
    const data = new TextEncoder().encode(value);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  }

  return simpleHash(value);
};

export const setAmplitudeUserId = (userId?: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (!amplitudeInitialized) {
    initAnalytics();
  }

  amplitude.setUserId(userId);
};

export const setAmplitudeUserProperties = (properties: Record<string, any>) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (!amplitudeInitialized) {
    initAnalytics();
  }

  const identify = new Identify();
  Object.entries(properties).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    if (Array.isArray(value) && value.length === 0) {
      return;
    }

    identify.set(key, value);
  });

  amplitude.identify(identify);
};

export const resetAmplitudeIdentity = () => {
  if (typeof window === 'undefined') {
    return;
  }

  if (!amplitudeInitialized) {
    initAnalytics();
  }

  amplitude.reset();
};
